<template>
  <div class="print-cover-container second-page">
    <div v-if="theme_option" class="overlay print-margin-sub" :style="backgroundImageStyle"></div>

    <div class="print-margin">
      <h2 class="print-subname" :style="subnameStyle">
        <span v-if="lang === 'KOR'"> INDEX </span>
        <span v-else> INDEX </span>
      </h2>
    </div>
    <div v-for="(group, groupIdx) in groupedToc" :key="'group' + groupIdx">
      <b-row class="print-margin">
        <b-col cols="12">
          <b-row align-v="end">
            <b-col
              v-for="(lv1_info, lv1_idx) in group"
              :key="'dropdown_lv1' + lv1_idx"
              :cols="group.length <= 4 ? '3' : '2'"
            >
              <template
                v-if="
                  !selectedLv1PrintOption ||
                  selectedLv1PrintOption.length === 0 ||
                  selectedLv1PrintOption.map(lv1_info => lv1_info.key).includes(lv1_info?.key)
                "
              >
                <h4 class="print-lv1" :style="nameStyle">
                  <span v-if="lang === 'KOR'">
                    {{ lv1_info.name }}
                  </span>
                  <span v-else>
                    {{ lv1_info.name_em ? lv1_info.name_em : lv1_info.name_ea }}
                  </span>
                </h4>
              </template>
            </b-col>
          </b-row>
          <b-row>
            <b-col
              v-for="(lv1_info, lv1_idx) in group"
              :key="'dropdown_lv1' + lv1_idx"
              :cols="group.length <= 4 ? '3' : '2'"
            >
              <template
                v-if="
                  !selectedLv1PrintOption ||
                  selectedLv1PrintOption.length === 0 ||
                  selectedLv1PrintOption.map(lv1_info => lv1_info.key).includes(lv1_info?.key)
                "
              >
                <div
                  v-for="(lv2_info, lv2_idx) in lv1_info.children"
                  :key="'dropdown_lv2' + lv2_idx"
                  class="d-flex flex-column"
                >
                  <h5
                    v-if="
                      lv2_info.appendix_type === null ||
                      report_types.includes(lv2_info.appendix_type)
                    "
                    class="print-lv2"
                    :style="dateStyle"
                  >
                    <a
                      v-if="lang === 'KOR'"
                      :href="'#' + lv1_info.name + '_' + lv2_info.name"
                      :style="dateStyle"
                    >
                      {{ lv2_info.name }}
                    </a>
                    <a v-else :href="'#' + lv1_info.name + '_' + lv2_info.name" :style="dateStyle">
                      {{ lv2_info.name_em ? lv2_info.name_em : lv2_info.name_ea }}
                    </a>
                  </h5>
                </div>
              </template>
            </b-col>
          </b-row>
        </b-col>
      </b-row>
    </div>

    <div class="d-flex justify-content-center align-items-end print-margin">
      <b-img :src="$store.state.static_host + selected_report.logo_path" class="pdf-cover-logo" />
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';

export default {
  name: 'PdfIndex',
  computed: {
    ...mapState('report_list', ['lang']),
    ...mapState('report_contents', ['theme_option']),
    ...mapGetters('report_list', ['selected_report']),
    ...mapState('story_board', ['main_setting']),
    ...mapState('assurance', ['assurance_status', 'latest_assurance']),
    groupedToc() {
      const groupSize = 6;
      const groups = [];
      for (let i = 0; i < this.selectedLv1PrintOption.length; i += groupSize) {
        groups.push(this.selectedLv1PrintOption.slice(i, i + groupSize));
      }
      return groups;
    },

    report_types() {
      const user_selected_report_type = Object.keys(this.selected_report.report_type).map(
        item => item.split('v')[0]
      );
      if (this.latest_assurance) {
        user_selected_report_type.push('ASSURANCE');
      }

      if (this.selected_report?.data_center) {
        user_selected_report_type.push('DATACENTER');
      }
      return user_selected_report_type;
    },

    nameStyle() {
      if (this.theme_option?.theme_info && this.selected_report) {
        const style = {
          fontFamily: `${this.theme_option?.theme_info?.title_font}`,
          color: `${this.selected_report.main_color}`
        };
        if (this.sb_toc.length > 5) {
          style.fontSize = this.lv1FontSize;
        } else {
          style.fontSize = '20px';
        }
        return style;
      }
    },

    subnameStyle() {
      if (this.theme_option?.theme_info && this.selected_report) {
        return {
          fontFamily: `${this.theme_option?.theme_info?.title_font}`,
          color: `${this.selected_report.main_color}`
        };
      }
    },

    dateStyle() {
      if (this.theme_option?.theme_info && this.selected_report) {
        const style = {
          fontFamily: `${this.theme_option?.theme_info?.title_font}`,
          color: `${this.selected_report.sub_color} !important`,
          textDecoration: 'none'
        };
        if (this.sb_toc.length > 5) {
          style.fontSize = this.submenuFontSize;
        } else {
          style.fontSize = '17px';
        }
        return style;
      }
    },

    backgroundImageStyle() {
      if (this.main_setting?.main_business_info?.unsplash_path) {
        return {
          background: `url(${this.main_setting?.main_business_info?.unsplash_path}) center center / cover no-repeat`,
          maxHeight: '700px'
        };
      } else if (this.main_setting?.main_business_info?.image_path) {
        const normalizedImagePath = this.main_setting.main_business_info.image_path.replace(
          /\\/g,
          '/'
        );
        return {
          background: `url(${
            this.$store.state.static_host + normalizedImagePath
          }) center center / cover no-repeat`,
          maxHeight: '700px'
        };
      } else {
        return {
          backgroundColor: this.selected_report?.sub_color,
          minHeight: 'auto',
          width: '100%',
          maxHeight: '700px'
        };
      }
    },

    lv1FontSize() {
      const lv1Count = this.sb_toc.length;
      const extra = Math.max(0, lv1Count - 5);
      const multiplier = 1 - 0.1 * extra;
      const offset = this.lang === 'ENG' ? 2 : 0;
      const width = window.innerWidth;
      let fontSize;
      if (width >= 1920) {
        fontSize = Math.round(20 * multiplier) - offset;
      } else if (width >= 1680) {
        fontSize = Math.round(20 * multiplier) - offset;
      } else if (width >= 1440) {
        fontSize = Math.round(20 * multiplier) - offset;
      } else {
        fontSize = Math.round(20 * multiplier) - offset;
      }
      return fontSize + 'px';
    },
    submenuFontSize() {
      const lv1Count = this.sb_toc.length;
      const extra = Math.max(0, lv1Count - 5);
      const multiplier = 1 - 0.1 * extra;
      const offset = this.lang === 'ENG' ? 2 : 0;
      const width = window.innerWidth;
      let fontSize;
      if (width >= 1920) {
        fontSize = Math.round(17 * multiplier) - offset;
      } else if (width >= 1680) {
        fontSize = Math.round(17 * multiplier) - offset;
      } else if (width >= 1440) {
        fontSize = Math.round(17 * multiplier) - offset;
      } else {
        fontSize = Math.round(17 * multiplier) - offset;
      }
      return fontSize + 'px';
    }
  },
  props: {
    sb_toc: { type: Array, default: [] },
    selectedLv1PrintOption: { type: Array, default: () => [] }
  }
};
</script>

<style lang="scss" scoped>
.print-cover-container {
  padding-top: 0px;
  padding-bottom: 0px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: space-between;
  height: 100vh;
}

.print-subname {
  font-size: 2.75rem;
  font-weight: 600;
  margin-bottom: 0;
  line-height: 1;
}

.print-date {
  font-size: 1.2rem;
  font-weight: 400;
  line-height: 1.7;
}

.print-lv1 {
  font-weight: 600;
  line-height: 1.2;
}

.print-lv2 {
  font-weight: 400;
  line-height: 1.2;
}

.pdf-cover-logo {
  width: 175px;
}
.overlay {
  min-height: 700px;
  background-position: center center;
  background-repeat: no-repeat;
  background-origin: border-box;
  background-size: cover;
}
</style>
